import type { VariantProps } from "@-/com.npmjs.cva";
import type { ViewProps } from "@-/com.npmjs.react-native";
import { AlertDialogPortal } from "#alert-dialog-primitives";
import { buttonTextVariants, buttonVariants } from "@-/dev.improve.haniverse.components.ui.button";
import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import { TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  Action,
  Cancel,
  Content,
  Description,
  Overlay,
  Title,
  type ActionProps,
  type ActionRef,
  type CancelProps,
  type CancelRef,
  type ContentProps,
  type ContentRef,
  type DescriptionProps,
  type DescriptionRef,
  type OverlayProps,
  type OverlayRef,
  type TitleProps,
  type TitleRef,
  useRootContext,
} from "@rn-primitives/alert-dialog";
import { Fragment, type ReactNode, type RefAttributes } from "react";
import { Modal, Platform, View } from "@-/com.npmjs.react-native";
import { FadeIn, FadeOut } from "@-/com.npmjs.react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "@-/com.npmjs.react-native-screens";

/**
 * Android's counterpart of iOS's `FullWindowOverlay`: the dialog in its own window, above
 * everything else in the activity.
 *
 * Without it the dialog renders in the app-root portal host, and every @gorhom bottom sheet sits
 * ABOVE that host (`BottomSheetModalProvider` portals its sheets after the app tree). A dialog a
 * sheet opened was therefore drawn dimmed under the sheet's backdrop, and its buttons were
 * unreachable: the sign-in replace warning's "Sign in anyway" landed on the auth sheet's
 * backdrop, dismissed the sheet and dropped the sign-in.
 *
 * A native dialog window does not take the hardware back press through `BackHandler`, which is how
 * rn-primitives closes the dialog, so the window forwards it to the dialog's own open state.
 *
 * The WINDOW fades (`animationType="fade"`, a system window animation). The content inside it must
 * never wait on a Reanimated layout animation: staging run 37115091190 opened "Turn On Automatic
 * Sync?" and its window held only the bare overlay at opacity 0 — Reanimated's `entering` FadeIn
 * never brought it in, so an invisible window swallowed every tap. See {@link AlertDialogOverlay}.
 */
function AndroidDialogWindow({ children }: { children?: ReactNode }) {
  const { onOpenChange } = useRootContext();
  return (
    <Modal
      animationType="fade"
      navigationBarTranslucent
      onRequestClose={() => {
        onOpenChange(false);
      }}
      statusBarTranslucent
      testID="alert-dialog-window"
      transparent
      visible
    >
      {children}
    </Modal>
  );
}

/** Lifts the dialog above native presentation: a full-window overlay on iOS, its own window on Android. */
function FullWindowOverlay({ children }: { children?: ReactNode }) {
  if (Platform.OS === "android") {
    return <AndroidDialogWindow>{children}</AndroidDialogWindow>;
  }
  if (Platform.OS === "ios") {
    return <RNFullWindowOverlay>{children}</RNFullWindowOverlay>;
  }
  return <Fragment>{children}</Fragment>;
}

export function AlertDialogOverlay({
  children,
  className,
  ...props
}: Omit<OverlayProps, "asChild"> &
  RefAttributes<OverlayRef> & {
    children?: ReactNode;
  }) {
  return (
    <FullWindowOverlay>
      <Overlay
        className={cn(
          "absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-2",
          Platform.select({
            web: "animate-in fade-in-0 fixed",
          }),
          className,
        )}
        {...props}
        asChild={Platform.OS !== "web"}
      >
        {Platform.OS === "android" ? (
          // Plain views: the dialog window fades itself (see `AndroidDialogWindow`), and a layout
          // animation that never completes there leaves the dialog invisible but tap-blocking.
          <View>{children}</View>
        ) : (
          <NativeOnlyAnimatedView entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)}>
            <NativeOnlyAnimatedView entering={FadeIn.delay(50)} exiting={FadeOut.duration(150)}>
              <>{children}</>
            </NativeOnlyAnimatedView>
          </NativeOnlyAnimatedView>
        )}
      </Overlay>
    </FullWindowOverlay>
  );
}

export function AlertDialogContent({
  className,
  portalHost,
  ...props
}: ContentProps &
  RefAttributes<ContentRef> & {
    portalHost?: string;
  }) {
  return (
    <AlertDialogPortal hostName={portalHost}>
      <AlertDialogOverlay>
        <Content
          className={cn(
            "z-50 mx-4 flex w-full max-w-full min-w-0 shrink flex-col items-stretch gap-4 rounded-xs border border-border bg-background p-6 shadow-lg shadow-black/5 sm:mx-auto sm:max-w-lg",
            Platform.select({
              web: "animate-in fade-in-0 zoom-in-95 duration-200",
            }),
            className,
          )}
          {...props}
        />
      </AlertDialogOverlay>
    </AlertDialogPortal>
  );
}

export function AlertDialogHeader({ className, ...props }: ViewProps) {
  return (
    <TextClassContext value="text-center sm:text-left">
      <View className={cn("flex flex-col gap-2", className)} {...props} />
    </TextClassContext>
  );
}

export function AlertDialogFooter({ className, ...props }: ViewProps) {
  return (
    <View
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

export function AlertDialogTitle({ className, ...props }: TitleProps & RefAttributes<TitleRef>) {
  return <Title className={cn("text-lg font-semibold text-foreground", className)} {...props} />;
}

export function AlertDialogDescription({
  className,
  ...props
}: DescriptionProps & RefAttributes<DescriptionRef>) {
  return <Description className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export function AlertDialogAction({
  className,
  variant = "default",
  ...props
}: ActionProps & RefAttributes<ActionRef> & VariantProps<typeof buttonVariants>) {
  return (
    <TextClassContext value={buttonTextVariants({ variant })}>
      <Action className={cn(buttonVariants({ variant }), className)} {...props} />
    </TextClassContext>
  );
}

export function AlertDialogCancel({
  className,
  variant = "outline",
  ...props
}: CancelProps & RefAttributes<CancelRef> & VariantProps<typeof buttonVariants>) {
  return (
    <TextClassContext value={buttonTextVariants({ variant })}>
      <Cancel className={cn(buttonVariants({ variant }), className)} {...props} />
    </TextClassContext>
  );
}
